import React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { CheckCircle, ErrorOutline, WarningRounded } from "@mui/icons-material";
import { Field, resolveUrl } from "./Field";
import UriLink from "./UriLink";
import { indexabilityText } from "./indexability";

function CanonicalDiff({ canonicalUrl, pageUri }) {
    const resolved = resolveUrl(pageUri, canonicalUrl);
    return (
        <DiffWrap>
            <DiffRow>
                <DiffLabel>This page</DiffLabel>
                <DiffUrl>{pageUri}</DiffUrl>
            </DiffRow>
            <DiffRow>
                <DiffLabel>Canonical</DiffLabel>
                <DiffUrl>
                    <UriLink url={resolved} displayText={canonicalUrl} />
                </DiffUrl>
            </DiffRow>
        </DiffWrap>
    );
}

const IndexabilityField = ({ cur }) => {
    const pageUri = useSelector(s => s.currentMonitoredUri.uri);
    const indexability = cur?.indexability;

    if (!indexability || indexability.indexable === undefined || indexability.indexable === null) {
        return (
            <Field diffDisabled fieldName="Indexability"
                fieldValueCur={<><WarningRounded style={{ color: "gray", marginRight: "0.4rem" }} /> Unknown</>}
            />
        );
    }

    const isIndexable = indexability.indexable;
    const notIndexableReasons = indexability.notIndexableReasons || [];
    const isCanonicalized = notIndexableReasons.includes("CANONICALIZED");
    const canonicalUrl = isCanonicalized && cur.canonicals?.[0];

    const icon = isIndexable
        ? <CheckCircle style={{ color: "green", marginRight: "0.4rem" }} />
        : <ErrorOutline style={{ color: "orange", marginRight: "0.4rem" }} />;

    const text = indexabilityText(indexability);

    return (
        <Field diffDisabled fieldName="Indexability"
            fieldValueCur={
                <>
                    {icon} {text}
                    {isCanonicalized && canonicalUrl && pageUri && (
                        <CanonicalDiff pageUri={pageUri} canonicalUrl={canonicalUrl} />
                    )}
                </>
            }
        />
    );
};

export default IndexabilityField;

const DiffWrap = styled.div`
    display: table;
    width: 100%;
    margin-top: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    font-size: 0.75rem;
`;

const DiffRow = styled.div`
    display: table-row;
    &:first-child > * { background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
    &:last-child  > * { background: #eef2ff; }
`;

const DiffLabel = styled.span`
    display: table-cell;
    font-size: 0.65rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    white-space: nowrap;
    padding: 5px 8px 5px 10px;
    vertical-align: baseline;
`;

const DiffUrl = styled.span`
    display: table-cell;
    word-break: break-all;
    line-height: 1.5;
    color: #0f172a;
    padding: 5px 10px 5px 0;
    vertical-align: baseline;
`;

const DiffChar = styled.span`
    background: ${({ $added, $removed }) => $added ? '#c7d2fe' : $removed ? '#fef3c7' : 'transparent'};
    ${({ $removed }) => $removed && 'text-decoration: line-through; opacity: 0.7;'}
    border-radius: 2px;
`;
